/**
 * First-party account API on the central IdP (auth.apiboo.com/api/account/*).
 * The IdP owns the user's linked accounts (notably GitHub: identity + the
 * repo-scoped token for git-sync); this reads them with the app's access
 * token and mirrors GitHub into the local accountsStore so Settings and
 * workspace git-sync keep working unchanged.
 *
 * Routed through Tauri's Rust HTTP (no CORS).
 */

import axios from 'axios';
import { tauriAxiosAdapter } from '../utils/tauriAxiosAdapter';
import { AUTH_BASE } from './oidcService';
import { getAccessToken } from './tokenRefresher';
import { setGitHubAccount, clearGitHubAccount, getGitHubAccount } from './accountsStore';

const idp = axios.create({
  baseURL: `${AUTH_BASE}/api/account`,
  timeout: 12000,
  adapter: tauriAxiosAdapter,
  headers: { 'Content-Type': 'application/json' },
});

idp.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/** Fetch linked accounts from the IdP. `{ github: {login, avatar_url, token} | null }`. */
export async function fetchLinkedAccounts() {
  const { data } = await idp.get('/linked');
  return data;
}

/**
 * Upload a profile picture (png/jpeg/webp/gif ≤ 5 MB). The IdP stores it in
 * object storage and serves it at the stable `/avatars/{id}` URL.
 * Returns `{ picture, avatar_url }`.
 */
export async function uploadAvatar(file) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await idp.post('/avatar', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Remove the uploaded picture — the avatar URL falls back to GitHub/pixel art. */
export async function deleteAvatar() {
  await idp.delete('/avatar');
}

/** Unlink GitHub at the IdP, then drop the local mirror. */
export async function unlinkGitHubAtIdp() {
  await idp.delete('/github');
  await clearGitHubAccount();
}

/**
 * Pull the IdP's linked accounts and mirror GitHub into the local
 * accountsStore (shape expected by githubAuth/workspaceGitSync). Best-effort —
 * never throws; leaves the existing local link untouched on network failure.
 */
export async function syncLinkedAccountsFromIdp() {
  try {
    const { github } = await fetchLinkedAccounts();

    if (github && github.token) {
      await setGitHubAccount({
        token: github.token,
        login: github.login || '',
        avatarUrl: github.avatar_url || '',
        linkedAt: Date.now(),
        source: 'idp',
      });
    } else {
      // The IdP is the source of truth: a link mirrored from the IdP that is
      // now gone there should be cleared locally too. A purely local link
      // (device-flow, no source) is left alone.
      const existing = await getGitHubAccount();
      if (existing?.source === 'idp') await clearGitHubAccount();
    }

    return github || null;
  } catch (e) {
    console.warn('[idpAccountService] linked-accounts sync failed (ignored):', e?.message || e);
    return undefined;
  }
}
